import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Legacy JS pages (existing)
// @ts-ignore
import Home from './pages/Home';
// @ts-ignore
import Login from './pages/Login';
// @ts-ignore
import Cadastro from './pages/Cadastro';
// @ts-ignore
import Onboarding from './pages/Onboarding';
// @ts-ignore
import Assinatura from './pages/Assinatura';
// @ts-ignore
import Contato from './pages/Contato';
// @ts-ignore
import Privacidade from './pages/Privacidade';
// @ts-ignore
import Termos from './pages/Termos';
// @ts-ignore
import ResetPassword from './pages/ResetPassword';

// New TypeScript pages
import Dashboard from './pages/Dashboard';
import IACoach from './pages/IACoach';
import Profile from './pages/Profile';
import Sessions from './pages/Sessions';
import Stats from './pages/Stats';
import Treinos from './pages/Treinos';

/* ── ErrorBoundary to catch silent crashes (shows error instead of black screen) ── */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            background: '#0d0d0d',
            color: '#fff',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, monospace',
            padding: 40,
          }}
        >
          <div style={{ maxWidth: 600, textAlign: 'center' }}>
            <h1 style={{ color: '#e53935', fontSize: 20, marginBottom: 16 }}>
              Algo deu errado
            </h1>
            <pre
              style={{
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: 8,
                padding: 16,
                textAlign: 'left',
                fontSize: 13,
                color: '#f5c800',
                overflow: 'auto',
                maxHeight: 300,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 16,
                background: '#00e676',
                color: '#000',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  return token ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/cadastro"
        element={
          <PublicRoute>
            <Cadastro />
          </PublicRoute>
        }
      />

      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <Onboarding />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <PrivateRoute>
            <Sessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/treinos"
        element={
          <PrivateRoute>
            <Treinos />
          </PrivateRoute>
        }
      />
      <Route
        path="/coach"
        element={
          <PrivateRoute>
            <IACoach />
          </PrivateRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <PrivateRoute>
            <Stats />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/assinatura"
        element={
          <PrivateRoute>
            <Assinatura />
          </PrivateRoute>
        }
      />

      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/contato" element={<Contato />} />
      <Route path="/privacidade" element={<Privacidade />} />
      <Route path="/termos" element={<Termos />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
